import { IonButton, IonButtons, IonCardContent, IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonRow, IonSpinner, IonText } from '@ionic/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { refresh } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';

import { ILabourClocking } from '../../../models/dms/clocking.model';
import { IRepairOrder } from '../../../models/dms/repairOrder.model';
import { IWorkshopVehicleDetails } from '../../../models/dms/workshopVehicle.model';
import store from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setLabourClockingRepairOrderStoreAsync } from '../../../store/technicianClockingSlice';
import { fetchWorkshopVehicleDetailsByRegistrationNumberStoreAsync, setSelectedWorkshopVehicleStoreAsync } from '../../../store/workshopVehicleSlice';
import { IonListWrapper } from './WorkshopVehicleAssetDetails.styles';
import { AttachmentAssetType, IAttachmentContainer } from '../../../models/dms/attachment.model';
import { setSelectedVehicleAttachmentsStoreAsync } from '../../../store/vehicleAttachmentSlice';
import SelectedVehicleAttachments from '../../selectedVehicleAttachments/SelectedVehicleAttachments';

interface IComponentProps {
  repairOrder: IRepairOrder;
}

const WorkshopVehicleAssetDetails: React.FC<IComponentProps> = ({ repairOrder }) => {
  const [loadingError, setLoadingError] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const { selectedWorkshopVehicle, selectedWorkshopVehicleLoading } = useAppSelector((state) => state.workshopVehicle);
  const { networkConnected } = useAppSelector((state) => state.app);

  const dispatch = useAppDispatch();

  useEffect(() => {
    loadRepairOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRepairOrder = async () => {
    const { selectedWorkshopVehicle } = store.getState().workshopVehicle;
    const { selectedVehicleAttachments } = store.getState().vehicleAttachments;

    // There is a chance that the registration number, repair order vehicle (and other details) will be null. This happens when the RO is clocked on
    // automatically from the server or from clocking history and no info is available in the cache to fill this information.

    await dispatch(
      setSelectedVehicleAttachmentsStoreAsync({
        assetType: AttachmentAssetType.WorkshopVehicle,
        key: repairOrder.vehicle.sourceRef,
        jobCode: '',
        jobType: '',
        repairOrderNumber: repairOrder.repairOrderNumber?.toString() || '-1',
        mediaRef: '',
        attachments: selectedVehicleAttachments?.key === repairOrder.vehicle.sourceRef ? [...selectedVehicleAttachments.attachments] : [],
      } as IAttachmentContainer),
    );

    if (repairOrder.vehicle.sourceRef) {
      // Rego is available
      if (repairOrder.vehicle.sourceRef.toLowerCase() !== selectedWorkshopVehicle?.registrationNumber?.toLowerCase()) {
        // Reset vehicle details to default
        dispatch(
          setSelectedWorkshopVehicleStoreAsync({
            registrationNumber: repairOrder.vehicle.sourceRef,
          } as IWorkshopVehicleDetails),
        );
        // Load workshop vehicle
        await loadWorkshopVehicle(repairOrder.vehicle.sourceRef, repairOrder.repairOrderNumber || -1);
      }
    } else {
      // Rego (and other values) are unavailable. Load the repair order to get the rego
      try {
        if (repairOrder.repairOrderNumber) {
          setLoadingData(true);
          const resultAction = await dispatch(setLabourClockingRepairOrderStoreAsync(repairOrder.repairOrderNumber));
          const result = unwrapResult(resultAction) as ILabourClocking;
          loadWorkshopVehicle(result.repairOrder.vehicle.sourceRef, repairOrder.repairOrderNumber);
        }
      } catch (ex) {
        setLoadingError(true);
      } finally {
        setLoadingData(false);
      }
    }
  };

  const loadWorkshopVehicle = async (registrationNumber: string, repairOrderNumber: number) => {
    try {
      setLoadingData(true);
      const resultAction = await dispatch(fetchWorkshopVehicleDetailsByRegistrationNumberStoreAsync({ registrationNumber: registrationNumber, repairOrderNumber: repairOrderNumber }));
      unwrapResult(resultAction);
      setLoadingError(false);
    } catch (ex) {
      setLoadingError(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleClickLoadRepairOrderDetails = async () => {
    await loadWorkshopVehicle(repairOrder.vehicle.sourceRef, repairOrder.repairOrderNumber || -1);
  };

  return (
    <IonListWrapper>
      {/* Header */}
      <IonItem>
        <IonButtons slot="end">
          <IonButton onClick={() => handleClickLoadRepairOrderDetails()} fill="clear" disabled={selectedWorkshopVehicleLoading || loadingData}>
            <IonIcon icon={refresh} slot="icon-only" color="primary" />
          </IonButton>
        </IonButtons>
        Vehicle Details
      </IonItem>
      {/* Data */}
      <IonItem>
        <IonCardContent>
          <IonGrid>
            <IonRow>
              <IonCol size="auto">
                <IonItem>
                  <IonLabel position="stacked" style={{ whiteSpace: 'nowrap' }}>
                    Registration Number
                  </IonLabel>
                  <IonText class="flexed flexed-center">{selectedWorkshopVehicle?.registrationNumber || ''}</IonText>
                  {(selectedWorkshopVehicleLoading || loadingData) && <IonSpinner name="dots" className="text-spinner"></IonSpinner>}
                </IonItem>
              </IonCol>
              <IonCol size="auto">
                <IonItem>
                  <IonLabel position="stacked">Make</IonLabel>
                  <IonText class="flexed flexed-center">{selectedWorkshopVehicle?.make || ''}</IonText>
                  {(selectedWorkshopVehicleLoading || loadingData) && <IonSpinner name="dots" className="text-spinner"></IonSpinner>}
                </IonItem>
              </IonCol>
              <IonCol size="auto">
                <IonItem>
                  <IonLabel position="stacked">Model</IonLabel>
                  <IonText class="flexed flexed-center">{selectedWorkshopVehicle?.model?.code || ''}</IonText>
                  {(selectedWorkshopVehicleLoading || loadingData) && <IonSpinner name="dots" className="text-spinner"></IonSpinner>}
                </IonItem>
              </IonCol>
              {/* <IonCol>
                <IonItem>
                  <IonLabel position="stacked">Odometer</IonLabel>
                  <IonText class="flexed flexed-center">{selectedWorkshopVehicle?.odometer?.reading || ''}</IonText>
                  {(selectedWorkshopVehicleLoading || loadingData) && <IonSpinner name="dots" className="text-spinner"></IonSpinner>}
                </IonItem>
              </IonCol> */}
            </IonRow>
          </IonGrid>
        </IonCardContent>
      </IonItem>
      {selectedWorkshopVehicle?.registrationNumber && repairOrder.repairOrderNumber && <SelectedVehicleAttachments />}
      {loadingError && (
        <IonItem lines="none" disabled={selectedWorkshopVehicleLoading || loadingData}>
          <IonText color="danger">{networkConnected && <h4>Failed to load vehicle details</h4>}</IonText>
        </IonItem>
      )}
    </IonListWrapper>
  );
};

export default WorkshopVehicleAssetDetails;
